import React from 'react'
import { Link } from '@tanstack/react-router'
import { useAuth } from '@/hooks/useAuth'
import {
  Receipt,
  Users,
  FileText,
  CreditCard,
  TrendingUp,
  DollarSign,
  BarChart3,
  ArrowRight
} from 'lucide-react'
import { DREControlsPanel } from './dre/components/DREControlsPanel'
import { usePageTitle } from '@/stores/page-title-store'
import { useFilterSync } from './shared/hooks/useFilterSync'
import type { PeriodDates } from './dre/components/PeriodPresetSelector'

export default function FinancialDashboard() {
  const { user } = useAuth()
  usePageTitle('Financeiro')

  // Filter state management with URL sync
  const { state: filterState, setFilter, setFilters } = useFilterSync({
    defaults: {
      startDate: '2025-01-01',
      endDate: '2025-12-31',
      granularity: 'yearly',
      comparisonMode: 'none',
      chartType: 'bar',
      selectedDimensions: [],
    },
  })

  // Handler for period changes from DREControlsPanel
  const handlePeriodChange = (dates: PeriodDates) => {
    setFilters({
      startDate: dates.startDate,
      endDate: dates.endDate,
    })
  }

  const modules = [
    {
      title: 'DRE - Resultado do Exercício',
      description: 'Análise completa de receitas, despesas e resultados',
      icon: Receipt,
      href: '/financial/dre',
      color: 'from-blue-500/10 to-blue-600/5 border-blue-500/20',
      iconColor: 'text-blue-400',
      roles: ['admin', 'diretor', 'ceo']
    },
    {
      title: 'Matriz de Rentabilidade',
      description: 'Receitas, custos e margens por cliente',
      icon: Users,
      href: '/financial/client-matrix',
      color: 'from-purple-500/10 to-purple-600/5 border-purple-500/20',
      iconColor: 'text-purple-400',
      roles: ['admin', 'diretor', 'ceo']
    },
    {
      title: 'Resultado Consolidado',
      description: 'Visão geral de receitas, custos e resultados',
      icon: BarChart3,
      href: '/financial',
      color: 'from-yellow-500/10 to-yellow-600/5 border-yellow-500/20',
      iconColor: 'text-yellow-400',
      roles: ['admin', 'diretor', 'ceo']
    }
  ]

  // Filter modules based on user role
  const accessibleModules = modules.filter(module =>
    !module.roles || module.roles.includes(user?.role || '')
  )

  // Admin-only access control
  if (!user || (user.role !== 'admin' && user.role !== 'diretor-executivo' && user.role !== 'diretor-financeiro' && user.role !== 'vice-presidente' && user.role !== 'ceo')) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6">
        <DollarSign className="h-16 w-16 text-brand-red" />
        <p className="text-3xl font-bold text-white font-display">Acesso Restrito</p>
        <p className="text-brand-gray-light text-center max-w-md font-body text-lg">
          Esta área contém informações financeiras sensíveis e está disponível apenas para administradores.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Controls */}
      <div className="flex flex-col items-end gap-4">
          <div className="text-sm text-brand-gray font-body bg-brand-dark-light/50 px-4 py-2 rounded-monumenta border border-gray-800/50">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-brand-primary rounded-full animate-pulse"></div>
              <span>Dados 2025 • Sistema SIG</span>
            </div>
          </div>

          {/* Filters Panel */}
          <DREControlsPanel
            startDate={filterState.startDate}
            endDate={filterState.endDate}
            onPeriodChange={handlePeriodChange}
            comparisonMode={filterState.comparisonMode}
            onComparisonModeChange={(mode) => setFilter('comparisonMode', mode)}
            granularity={filterState.granularity}
            onGranularityChange={(gran) => setFilter('granularity', gran)}
            showPeriod={true}
            showInterval={true}
            showComparison={false}
            showGranularity={false}
            showProaComparison={false}
            showPresets={false}
          />
      </div>

      {/* Info Card */}
      <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 p-6 rounded-monumenta border border-blue-500/20">
        <div className="flex items-start gap-4">
          <div className="bg-blue-500/20 p-3 rounded-monumenta">
            <TrendingUp className="h-6 w-6 text-blue-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">Feature Parity: SIG → monutrack</h3>
            <p className="text-brand-gray-light mb-4">
              Todos os módulos abaixo utilizam dados reais extraídos do sistema SIG (SQL Server).
              Os dados são estáticos (2025) e servem como base para desenvolvimento e testes.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-brand-gray mb-1">Total de Registros</div>
                <div className="text-white font-semibold">1,936 clientes</div>
              </div>
              <div>
                <div className="text-brand-gray mb-1">Receita Anual (2025)</div>
                <div className="text-green-400 font-semibold">R$ 96.2M</div>
              </div>
              <div>
                <div className="text-brand-gray mb-1">Resultado Líquido</div>
                <div className="text-purple-400 font-semibold">R$ 21.7M</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modules Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {accessibleModules.map((module) => {
          const Icon = module.icon

          return (
            <Link
              key={module.href}
              to={module.href}
              className={`group bg-gradient-to-br ${module.color} p-6 rounded-monumenta border hover:shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-monumenta bg-brand-dark-light/50 ${module.iconColor}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <ArrowRight className="h-5 w-5 text-brand-gray group-hover:text-white group-hover:translate-x-1 transition-all" />
              </div>

              <h3 className="text-xl font-bold text-white mb-2 group-hover:text-brand-primary transition-colors">
                {module.title}
              </h3>

              <p className="text-brand-gray-light text-sm">
                {module.description}
              </p>

              <div className="mt-4 pt-4 border-t border-gray-800/50">
                <span className="text-xs text-brand-gray uppercase tracking-wider">
                  Acesso: {module.roles?.join(', ') || 'Todos'}
                </span>
              </div>
            </Link>
          )
        })}
      </div>

      {/* Status Summary */}
      <div className="bg-brand-dark-light/50 p-6 rounded-monumenta border border-gray-800/50">
        <h3 className="text-lg font-semibold text-white mb-4">Status da Implementação</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="text-sm font-medium text-brand-gray-light mb-2">Completado</h4>
            <ul className="text-sm text-brand-gray space-y-1">
              <li>• Types TypeScript (1,179 linhas)</li>
              <li>• Mock APIs (Client Matrix + DRE)</li>
              <li>• Extração de dados 2025 (1,936 registros)</li>
              <li>• UI: DRE, Client Matrix, AR, AP</li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-medium text-brand-gray-light mb-2">Em Desenvolvimento</h4>
            <ul className="text-sm text-brand-gray space-y-1">
              <li>• Drill-down detalhado (50+ itens DRE)</li>
              <li>• Cálculos agregados de impostos (4 fontes)</li>
              <li>• Integração com Timesheet</li>
              <li>• FluxoGerencial (Cash Flow)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Documentation Link */}
      <div className="text-center p-6 bg-brand-dark-light/30 rounded-monumenta border border-gray-800/30">
        <p className="text-brand-gray-light text-sm">
          Para documentação completa, consulte:{' '}
          <code className="text-brand-primary">
            packages/entities/clients/monumenta/repos/monutrack/docs/SIG-MONUTRACK-FEATURE-PARITY-PLAN.md
          </code>
        </p>
      </div>
    </div>
  )
}
